import { useEffect, useRef, type RefObject } from 'react'

export type SessionListValues = Record<string, unknown>

/** Prefix for all list filter/search sessionStorage keys (cleared on logout). */
export const SESSION_LIST_STATE_PREFIX = 'deltrans:list-state:'

type UseSessionListStateOptions = {
  /** Storage key; falsy disables persist/restore. Prefer `sessionListStateKey(...)`. */
  key: string | null | undefined
  /** JSON-serializable values to persist (convert Dates with `.getTime()`) */
  values: SessionListValues
  /** Validate and apply restored fields */
  onRestore: (raw: SessionListValues) => void
}

/**
 * Build a namespaced sessionStorage key for list filter state.
 * Use `{module}` or `{module}-${entityId}` for the suffix.
 */
export function sessionListStateKey(suffix: string): string {
  return `${SESSION_LIST_STATE_PREFIX}${suffix}`
}

/**
 * Remove all persisted list filter/search state from sessionStorage.
 * Call on logout so the next user (or same user after re-login) does not inherit filters.
 */
export function clearSessionListStates(): void {
  if (typeof window === 'undefined') return
  const keysToRemove: string[] = []
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i)
    if (!key) continue
    if (
      key.startsWith(SESSION_LIST_STATE_PREFIX) ||
      // Legacy keys from before namespacing
      key.includes('-list-state') ||
      key.startsWith('financial-statements-state-') ||
      key.includes('-history-state')
    ) {
      keysToRemove.push(key)
    }
  }
  for (const key of keysToRemove) {
    sessionStorage.removeItem(key)
  }
}

/**
 * Persist list filter/search state in sessionStorage and restore on remount
 * (e.g. after navigating to a child create/edit page and back within the same tab).
 *
 * Naming convention: `sessionListStateKey('{module}')` or
 * `sessionListStateKey('{module}-${entityId}')`.
 * Persist only applied filters (committed search), not half-typed input.
 * Clearing search must write an empty string so restore stays cleared.
 */
export function useSessionListState({
  key,
  values,
  onRestore,
}: UseSessionListStateOptions): void {
  const valuesRef = useRef(values)
  valuesRef.current = values
  const onRestoreRef = useRef(onRestore)
  onRestoreRef.current = onRestore

  // Restore once per key so return-from-child does not get overwritten by defaults
  useEffect(() => {
    if (!key) return
    try {
      const raw = sessionStorage.getItem(key)
      if (!raw) return
      const parsed = JSON.parse(raw) as unknown
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return
      onRestoreRef.current(parsed as SessionListValues)
    } catch {
      // ignore invalid stored state
    }
  }, [key])

  // Persist after state has settled (deferred so restore setState wins over default write)
  const serialized = JSON.stringify(values)
  useEffect(() => {
    if (!key) return
    const id = setTimeout(() => {
      sessionStorage.setItem(key, JSON.stringify(valuesRef.current))
    }, 0)
    return () => clearTimeout(id)
  }, [key, serialized])
}

/** Restore a Date from a stored epoch-ms value. */
export function dateFromSessionValue(value: unknown): Date | null {
  if (typeof value !== 'number' || Number.isNaN(value)) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Sync an uncontrolled search input after restoring applied search. */
export function syncSearchInputRef(
  ref: RefObject<HTMLInputElement | null>,
  value: string,
): void {
  if (ref.current) {
    ref.current.value = value
  }
}
